import { Fragment, PureComponent, ReactNode } from 'react';
import { connect, useDispatch } from 'react-redux';
import { PatientDetailsVisits } from '../../../store/reducers/prescriptionPad';
import VisitsEmpty from './VisitsEmpty';
import { Tool } from '../../../store//types/prescription';
import { GlobalState } from '../../../store/reducers';
import { getPatientDetailsVisits } from '../../../store/selectors/prescriptionPad';
import moment from 'moment';
import { getPxElementConfig } from '../../../store/selectors/session';
import { GeniePadElementsSettingItem } from '../../../api/pushGenie';
import { filesCopyIcons } from '../../../components/common/Icons';
import VisitsShimmer from './VisitsShimmer';
import { useHistory, useParams } from 'react-router-dom';
import { PPRouterProps } from '../../../routes/paths';
import {
    getAllPadSections,
    checkIsEmpty,
    getAllPastVisitComponent,
    onCopy,
} from '../../../components/common/PastVisitUtils';
import { BusinessDoctorName, changePathAndFocus } from './magicBoxUtils';
import { generatePastVisitTableCellId } from '../../../components/prescriptionPad/rightPanel/workspace/PastVisits';
import { VISIT_TYPE } from '../../../api/fetchAllVisitsForPatient';
import { isBusinessVisit } from '../../utils';
import PatientSummaryComponent from './PatientSummaryComponent';

type Props = {
    visits?: PatientDetailsVisits;
    padElementsConfig: GeniePadElementsSettingItem[];
};

class Visits extends PureComponent<Props> {
    render(): ReactNode {
        const { visits, padElementsConfig } = this.props;

        return (
            <>
                <PatientSummaryComponent />
                {visits?.state === 'fetching' ? (
                    <VisitsShimmer />
                ) : !visits?.data?.length ? (
                    <VisitsEmpty />
                ) : (
                    visits.data.map((visit, index) => (
                        <Fragment>
                            <VisitSummary
                                key={visit.visitId}
                                visit={visit}
                                padElementsConfig={padElementsConfig}
                            />
                            {visits.data && index !== visits.data.length - 1 && (
                                <p className="w-full flex justify-center">
                                    <p className="h-4 rounded-full bg-darwin-neutral-50 w-full mx-8"></p>
                                </p>
                            )}
                        </Fragment>
                    ))
                )}
            </>
        );
    }
}

export type P = {
    visit: Tool & { date: string; visitId: string; type?: VISIT_TYPE; doc_name?: string };
    padElementsConfig: GeniePadElementsSettingItem[];
};

const copyIcon = filesCopyIcons();

const VisitSummary: React.FC<P> = ({ visit, padElementsConfig }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params: PPRouterProps = useParams();
    const { pxId, pxState } = params;

    // need to change this to vertical and horizontal scroll
    const goToVisit = () => {
        changePathAndFocus(
            generatePastVisitTableCellId(visit.visitId),
            history,
            pxId,
            pxState,
            'patient-overview',
            undefined,
            false,
        );
    };

    const allSections = getAllPadSections(padElementsConfig, visit.dyform);
    const components = getAllPastVisitComponent(allSections, visit, dispatch);
    const isEmpty = checkIsEmpty(allSections, visit);

    return (
        <div className="px-4 p-8 pt-7">
            <div className="space-y-4">
                <div className="p-5 flex justify-between items-center text-13 font-700">
                    <div className="flex-1 flex items-start space-x-5">
                        {isBusinessVisit(visit) && <BusinessDoctorName docName={visit.doc_name} />}
                        <p className="text-neutral-black">
                            {moment(visit.date).format('MMM DD, YYYY')}:{' '}
                            {visit.diagnosis?.map((d) => d.name).join(', ')}
                        </p>
                    </div>
                    {!isEmpty && (
                        <span
                            onClick={() => onCopy('PX', visit, allSections, dispatch)}
                            className="h-28 w-28 flex justify-center items-center bg-darwin-neutral-50 rounded-4 cursor-pointer"
                        >
                            {copyIcon}
                        </span>
                    )}
                </div>
                {components.length && components}
                {isEmpty && (
                    <div className="px-16" onClick={goToVisit}>
                        (
                        <button className="border-b border-transparent cursor-pointer text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700 focus:outline-none">
                            click here
                        </button>{' '}
                        to see the prescription)
                    </div>
                )}
            </div>
        </div>
    );
};

export default connect((state: GlobalState) => ({
    visits: getPatientDetailsVisits(state),
    padElementsConfig: getPxElementConfig(state),
}))(Visits);
